import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MainContainer from "../UI/MainContainer";
import Card from "../UI/Card";
import AddOrEditProfileModal from "../UI/AddOrEditProfileModal";
import axios from "../../store/api/axios";
import { Instrument, Post, UserDetails } from "../../types/Types";
import Button from "../UI/Button";
import { ButtonTypes, DEFAULT_AUTH_DATA } from "../../utils/Constants";
import { IoAdd, IoMailOutline } from "react-icons/io5";
import usePageTitle from "../../utils/usePageTitle";

const Profile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<UserDetails | null>(null);
  const [postsList, setPostsList] = useState<Post[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [instruments, setInstruments] = useState<{ value: string; label: string }[]>([]);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const authData = localStorage.getItem("auth_data") ? JSON.parse(localStorage.getItem("auth_data")!) : DEFAULT_AUTH_DATA;
  const isOwner = userId === String(authData?.id);
  usePageTitle(user?.username);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`/users/${userId}/`);
        setUser(response.data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    const fetchUserPosts = async () => {
      try {
        const postsResponse = await axios.get(`/users/${userId}/posts/`);
        setPostsList(postsResponse.data.reverse());
      } catch (error) {
        console.error("Error fetching user posts:", error);
      }
    };

    fetchUser();
    fetchUserPosts();
  }, [userId]);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        const response = await axios.get('/instruments/');
        setInstruments(response.data.map((inst: Instrument) => ({ value: inst.id, label: inst.name })));
      } catch (error) {
        console.error('Error fetching instruments:', error);
      }
    };

    fetchInstruments();
  }, []);

  const handleSave = async (updatedUser: UserDetails, avatarFile?: File | null) => {
    try {
      setIsSavingProfile(true);
      // "profile_photo" must never go through this JSON PUT: when unchanged
      // it's just the existing image URL (a string), and the backend's
      // ImageField rejects a string with a 400 ("not a file"). The avatar
      // has its own dedicated multipart PATCH below.
      const { profile_photo, ...updatedUserWithoutPhoto } = updatedUser;

      // The backend now fully replaces education/experience from whatever
      // list is sent (create/update/delete by id), so the full, current
      // list from the modal is exactly what should be sent — no need to
      // (buggily) guess which entries are "new" here.
      const response = await axios.put(`/users/${updatedUser.id}/`, updatedUserWithoutPhoto);

      let nextUser = response.data;

      if (avatarFile) {
        const fd = new FormData();
        fd.append("profile_photo", avatarFile);
        // const resPatch = await axios.patch(`/users/${updatedUser.id}/`, fd);
        const resPatch = await axios.patch(
          `/users/${updatedUser.id}/`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          }
        );
        nextUser = resPatch.data;
      }

      setUser(nextUser);
    } catch (error) {
      console.error("Error saving user data:", error);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleAudioPlay = (audio: HTMLAudioElement) => {
    if (audio) {
      audio.play();
    }
  };

  if (!user) {
    return (
      <MainContainer>
        <div className="w-full bg-white p-8" role="status" aria-label="Loading profile">
          <div className="bg-white rounded-3xl shadow-md overflow-hidden border border-gray-e8" aria-hidden="true">
            <div className="h-24 skeleton" />
            <div className="px-6 pb-6">
              <div className="flex gap-4 items-start">
                <div className="-mt-11 w-[88px] h-[88px] rounded-full border-4 border-white skeleton shrink-0" />
                <div className="flex-1 pt-3 space-y-2">
                  <div className="skeleton h-6 w-48 max-w-full rounded-md" />
                  <div className="skeleton h-4 w-64 max-w-full rounded-md" />
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <div className="skeleton h-4 w-full max-w-[520px] rounded-md" />
                <div className="skeleton h-4 w-2/3 max-w-[360px] rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </MainContainer>
    );
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
  const subtitle = [fullName, user.position].filter(Boolean).join(" · ");
  const instrumentNames = (user.instruments || [])
    .map((id: any) => instruments.find((i) => i.value === id)?.label)
    .filter(Boolean) as string[];

  // The owner gets a shortcut into the edit modal (which is where
  // education/experience are added); visitors just see a short note.
  const emptySection = (ownerText: string, buttonLabel: string) =>
    isOwner ? (
      <div className="border-2 border-dashed border-primary/25 rounded-[18px] p-[18px] flex flex-col items-start gap-2.5 bg-white/50">
        <p className="text-black41">{ownerText}</p>
        <Button type={ButtonTypes.PRIMARY} onClick={() => setIsModalOpen(true)} className="flex items-center !px-4 !py-2 text-sm font-semibold">
          <IoAdd className="mr-1 text-lg" />
          {buttonLabel}
        </Button>
      </div>
    ) : (
      <p className="text-black41">Nothing added yet.</p>
    );

  return (
    <MainContainer>
      <div className="flex flex-col items-center justify-center w-full h-full bg-white p-8">
        <div className="w-full flex flex-col gap-6">
          <div className="bg-white rounded-3xl shadow-md overflow-hidden border border-gray-e8">
            <div className="h-24 bg-gradient-to-r from-primary via-[#526554] to-secondary" />
            <div className="px-6 pb-6">
              <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
                <img
                  src={user.profile_photo || "/Images/UserProfile.png"}
                  alt="Profile"
                  className="-mt-11 w-[88px] h-[88px] rounded-full border-4 border-white bg-white object-cover shrink-0"
                />
                <div className="flex-1 min-w-[180px] pt-3">
                  <h1 className="text-2xl font-bold leading-tight break-words">{user.username}</h1>
                  {subtitle && <p className="text-gray-41">{subtitle}</p>}
                </div>
                {isOwner &&
                  <Button
                    type={ButtonTypes.DEFAULT}
                    onClick={() => setIsModalOpen(true)}
                    className="mt-3 px-5 py-2 text-sm font-semibold text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5"
                  >
                    Edit profile
                  </Button>
                }
              </div>

              <div className="mt-4 flex flex-col gap-3">
                {user.bio && <p className="text-lg max-w-[65ch] whitespace-pre-line">{user.bio}</p>}
                {instrumentNames.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {instrumentNames.map((name) => (
                      <span key={name} className="text-xs font-semibold text-primary bg-primary/[0.07] px-2.5 py-0.5 rounded-full">
                        {name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-70">No instruments</p>
                )}
                {user.email && (
                  <a
                    href={`mailto:${user.email}`}
                    className="inline-flex items-center gap-2 w-fit text-primary font-semibold hover:underline break-all"
                  >
                    <IoMailOutline className="text-lg shrink-0" />
                    {user.email}
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="grid 768:grid-cols-2 gap-6">
            <div className="bg-secondary p-4 rounded-3xl">
              <h2 className="text-2xl font-semibold mb-4">Education</h2>
              <div className="grid grid-cols-1 gap-4">
                {user.education && user.education.length > 0 ? (
                  user.education.map((edu) => (
                    <div
                      key={edu.id}
                      className="bg-white p-4 rounded-3xl shadow-md"
                    >
                      <h3 className="font-semibold">{edu.degree}</h3>
                      <p>{edu.institution}</p>
                      <p className="text-gray-600">
                        {edu.start_date} - {edu.end_date}
                      </p>
                    </div>
                  ))
                ) : (
                  emptySection("Add where you studied music.", "Add education")
                )}
              </div>
            </div>

            <div className="bg-secondary p-4 rounded-3xl">
              <h2 className="text-2xl font-semibold mb-4">Experiences</h2>
              <div className="grid grid-cols-1 gap-4">
                {user.experiences && user.experiences.length > 0 ? (
                  user.experiences.map((exp) => (
                    <div
                      key={exp.id}
                      className="bg-white p-4 rounded-3xl shadow-md"
                    >
                      <h3 className="font-semibold">{exp.position}</h3>
                      <p>{exp.company}</p>
                      <p className="text-gray-600">
                        {exp.start_date} - {exp.end_date}
                      </p>
                    </div>
                  ))
                ) : (
                  emptySection("Add bands, orchestras or studios you've worked with.", "Add experience")
                )}
              </div>
            </div>
          </div>

          <div className="bg-secondary p-4 rounded-3xl">
            <h2 className="text-2xl font-semibold mb-4">Activities</h2>
            <div className="grid grid-cols-1 gap-4">
              {postsList.length > 0 &&
                postsList.map((post) => (
                  <Card
                    key={post.id}
                    id={post.id}
                    title={post.title}
                    description={post.description}
                    instruments={post.instruments}
                    audioSrc={post.mp3_file}
                    isArchived={post.is_archived}
                    created_at={post.created_at}
                    onAudioPlay={handleAudioPlay}
                    cardClassName="w-full h-[280px]"
                    descClassName="line-clamp-3 h-[75px]"
                  />
                ))}
            </div>
          </div>
        </div>
      </div>

      <AddOrEditProfileModal
        user={user}
        isOpen={isModalOpen}
        instruments={instruments}
        onRequestClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isSaving={isSavingProfile}
      />
    </MainContainer>
  );
};

export default Profile;
